import React, { useState, useMemo, useRef } from 'react';
import { 
  X, 
  Calendar, 
  Download, 
  ChevronDown, 
  ChevronRight,
  Users,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
  Filter
} from 'lucide-react';
import { Task } from '../shared/types';
import { calculateTaskManpower } from '../shared/manpowerHelpers';

interface ResourceGanttViewProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
  projectName?: string;
  manpowerConstants?: any[];
}

interface WorkGroup {
  id: string;
  category: string;
  floor: string;
  building: string;
  tasks: Task[];
  startDate: Date;
  endDate: Date;
  resources: Record<string, number>; // labor type -> total days
  totalWorkerDays: number;
}

// Labor type colors
const LABOR_COLORS: Record<string, string> = {
  'Mason': 'bg-blue-500',
  'Helper': 'bg-green-500',
  'Carpenter': 'bg-amber-500',
  'Plumber': 'bg-cyan-500',
  'Electrician': 'bg-yellow-500',
  'Painter': 'bg-pink-500',
  'Tiler': 'bg-purple-500',
  'Welder': 'bg-red-500',
  'Fitter': 'bg-indigo-500',
  'default': 'bg-gray-500'
};

const getLaborColor = (labor: string): string => {
  return LABOR_COLORS[labor] || LABOR_COLORS['default'];
};

export const ResourceGanttView: React.FC<ResourceGanttViewProps> = ({
  isOpen,
  onClose,
  tasks,
  projectName = 'Project',
  manpowerConstants = []
}) => {
  const [selectedBuildings, setSelectedBuildings] = useState<Set<string>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [zoomLevel, setZoomLevel] = useState<'day' | 'week' | 'month'>('week');
  const [showFilters, setShowFilters] = useState(true);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Get unique buildings and categories
  const { buildings, categories } = useMemo(() => {
    const bldgs = new Set<string>();
    const cats = new Set<string>();
    tasks.forEach(t => {
      if (t.building) bldgs.add(t.building);
      if (t.category) cats.add(t.category);
    });
    return { 
      buildings: Array.from(bldgs).sort(), 
      categories: Array.from(cats).sort() 
    };
  }, [tasks]);

  // Initialize selection with all buildings/categories on first render
  useMemo(() => {
    if (selectedBuildings.size === 0 && buildings.length > 0) {
      setSelectedBuildings(new Set(buildings.slice(0, 1))); // Select first building by default
    }
    if (selectedCategories.size === 0 && categories.length > 0) {
      setSelectedCategories(new Set(categories.slice(0, 3))); // Select first 3 categories
    }
  }, [buildings, categories]);

  // Filter tasks based on selection
  const filteredTasks = useMemo(() => {
    if (selectedBuildings.size === 0 && selectedCategories.size === 0) {
      return [];
    }
    return tasks.filter(t => {
      const buildingMatch = selectedBuildings.size === 0 || selectedBuildings.has(t.building || '');
      const categoryMatch = selectedCategories.size === 0 || selectedCategories.has(t.category || '');
      return buildingMatch && categoryMatch;
    });
  }, [tasks, selectedBuildings, selectedCategories]);

  // Group tasks by Category + Floor and calculate resources
  const workGroups = useMemo((): WorkGroup[] => {
    const groups: Record<string, WorkGroup> = {};

    filteredTasks.forEach(task => {
      const category = task.category || 'Unknown';
      const floor = task.floor || 'N/A';
      const building = task.building || 'Unknown';
      const key = `${building}-${category}-${floor}`;

      if (!groups[key]) {
        groups[key] = {
          id: key,
          category,
          floor,
          building,
          tasks: [],
          startDate: new Date('2099-12-31'),
          endDate: new Date('1970-01-01'),
          resources: {},
          totalWorkerDays: 0
        };
      }

      const group = groups[key];
      group.tasks.push(task);

      // Calculate dates
      const taskStart = new Date(task.startDate || task.originalSchedule?.startDate || new Date());
      const taskEnd = new Date(task.finishDate || task.originalSchedule?.finishDate || new Date());
      
      if (taskStart < group.startDate) group.startDate = taskStart;
      if (taskEnd > group.endDate) group.endDate = taskEnd;

      // Calculate resources
      const manpower = calculateTaskManpower(task, manpowerConstants);
      if (manpower) {
        manpower.requirements.forEach(req => {
          if (!group.resources[req.labour]) {
            group.resources[req.labour] = 0;
          }
          group.resources[req.labour] += req.daysRequired;
          group.totalWorkerDays += req.daysRequired;
        });
      }
    });

    return Object.values(groups).sort((a, b) => {
      // Sort by building, then category, then floor
      if (a.building !== b.building) return a.building.localeCompare(b.building);
      if (a.category !== b.category) return a.category.localeCompare(b.category);
      return a.floor.localeCompare(b.floor);
    });
  }, [filteredTasks, manpowerConstants]);

  // Calculate timeline range
  const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
    if (workGroups.length === 0) {
      const today = new Date();
      return { 
        timelineStart: today, 
        timelineEnd: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000),
        totalDays: 30
      };
    }

    let minDate = new Date('2099-12-31');
    let maxDate = new Date('1970-01-01');

    workGroups.forEach(g => {
      if (g.startDate < minDate) minDate = g.startDate;
      if (g.endDate > maxDate) maxDate = g.endDate;
    });

    // Add padding
    const start = new Date(minDate);
    start.setDate(start.getDate() - 7);
    const end = new Date(maxDate);
    end.setDate(end.getDate() + 7);

    const days = Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));

    return { timelineStart: start, timelineEnd: end, totalDays: days };
  }, [workGroups]);

  // Generate time periods based on zoom level
  const timePeriods = useMemo(() => {
    const periods: { start: Date; end: Date; label: string }[] = [];
    const current = new Date(timelineStart);

    while (current < timelineEnd) {
      const periodStart = new Date(current);
      let periodEnd: Date;
      let label: string;

      if (zoomLevel === 'day') {
        periodEnd = new Date(current);
        periodEnd.setDate(periodEnd.getDate() + 1);
        label = current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else if (zoomLevel === 'week') {
        periodEnd = new Date(current);
        periodEnd.setDate(periodEnd.getDate() + 7);
        const endLabel = new Date(periodEnd);
        endLabel.setDate(endLabel.getDate() - 1);
        label = `${current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      } else {
        // Month
        periodEnd = new Date(current.getFullYear(), current.getMonth() + 1, 1);
        label = current.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }

      periods.push({ start: periodStart, end: periodEnd, label });
      current.setTime(periodEnd.getTime());
    }

    return periods;
  }, [timelineStart, timelineEnd, zoomLevel]);

  // Calculate bar position and width
  const getBarStyle = (startDate: Date, endDate: Date) => {
    const startOffset = Math.max(0, (startDate.getTime() - timelineStart.getTime()) / (24 * 60 * 60 * 1000));
    const duration = Math.max(1, (endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
    
    const leftPercent = (startOffset / totalDays) * 100;
    const widthPercent = (duration / totalDays) * 100;

    return {
      left: `${leftPercent}%`,
      width: `${Math.max(widthPercent, 1)}%`
    };
  };

  // Calculate daily resource requirements for the chart
  const dailyResources = useMemo(() => {
    const daily: Record<string, Record<string, number>> = {}; // date -> labor -> count

    workGroups.forEach(group => {
      const durationDays = Math.max(1, Math.ceil((group.endDate.getTime() - group.startDate.getTime()) / (24 * 60 * 60 * 1000)));
      
      Object.entries(group.resources).forEach(([labor, totalDays]) => {
        const workersPerDay = Math.ceil(totalDays / durationDays);
        
        // Distribute workers across the duration
        const current = new Date(group.startDate);
        while (current <= group.endDate) {
          const dateKey = current.toISOString().split('T')[0];
          if (!daily[dateKey]) daily[dateKey] = {};
          if (!daily[dateKey][labor]) daily[dateKey][labor] = 0;
          daily[dateKey][labor] += workersPerDay;
          current.setDate(current.getDate() + 1);
        }
      });
    });

    return daily;
  }, [workGroups]);

  // Find peak resources
  const peakResources = useMemo(() => {
    let maxTotal = 0;
    let peakDate = '';
    const laborPeaks: Record<string, { count: number; date: string }> = {};

    Object.entries(dailyResources).forEach(([date, resources]) => {
      let dayTotal = 0;
      Object.entries(resources).forEach(([labor, count]) => {
        dayTotal += count;
        if (!laborPeaks[labor] || count > laborPeaks[labor].count) {
          laborPeaks[labor] = { count, date };
        }
      });
      if (dayTotal > maxTotal) {
        maxTotal = dayTotal;
        peakDate = date;
      }
    });

    return { maxTotal, peakDate, laborPeaks };
  }, [dailyResources]);

  // Export to Excel (CSV format)
  const exportToExcel = () => {
    const rows: string[][] = [];
    
    // Header
    rows.push(['Resource Planning Export', projectName, new Date().toLocaleDateString()]);
    rows.push([]);
    
    // Summary section
    rows.push(['SUMMARY']);
    rows.push(['Total Work Groups', workGroups.length.toString()]);
    rows.push(['Total Tasks', filteredTasks.length.toString()]);
    rows.push(['Peak Workers', peakResources.maxTotal.toString(), 'on', peakResources.peakDate]);
    rows.push([]);

    // Peak by labor type
    rows.push(['PEAK RESOURCES BY TYPE']);
    rows.push(['Labor Type', 'Peak Count', 'Peak Date']);
    Object.entries(peakResources.laborPeaks).forEach(([labor, { count, date }]) => {
      rows.push([labor, count.toString(), date]);
    });
    rows.push([]);

    // Detailed breakdown
    rows.push(['DETAILED BREAKDOWN']);
    rows.push(['Building', 'Category', 'Floor', 'Tasks', 'Start Date', 'End Date', 'Duration (days)', ...Object.keys(peakResources.laborPeaks).map(l => `${l} (days)`)]);
    
    workGroups.forEach(group => {
      const duration = Math.ceil((group.endDate.getTime() - group.startDate.getTime()) / (24 * 60 * 60 * 1000));
      const laborDays = Object.keys(peakResources.laborPeaks).map(l => (group.resources[l] || 0).toString());
      rows.push([
        group.building,
        group.category,
        group.floor,
        group.tasks.length.toString(),
        group.startDate.toLocaleDateString(),
        group.endDate.toLocaleDateString(),
        duration.toString(),
        ...laborDays
      ]);
    });

    // Convert to CSV
    const csv = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resource-plan-${projectName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Toggle building/category selection
  const toggleBuilding = (building: string) => {
    setSelectedBuildings(prev => {
      const next = new Set(prev);
      if (next.has(building)) {
        next.delete(building);
      } else {
        next.add(building);
      }
      return next;
    });
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  if (!isOpen) return null;

  const columnWidth = zoomLevel === 'day' ? 40 : zoomLevel === 'week' ? 80 : 120;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1600px] h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-cyan-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Calendar className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Resource Timeline</h2>
                <p className="text-sm text-gray-600">
                  {filteredTasks.length} tasks • {workGroups.length} work groups
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
                <button
                  onClick={() => setZoomLevel('day')}
                  className={`px-3 py-1 text-xs font-medium rounded ${zoomLevel === 'day' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  Day
                </button>
                <button
                  onClick={() => setZoomLevel('week')}
                  className={`px-3 py-1 text-xs font-medium rounded ${zoomLevel === 'week' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  Week
                </button>
                <button
                  onClick={() => setZoomLevel('month')}
                  className={`px-3 py-1 text-xs font-medium rounded ${zoomLevel === 'month' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  Month
                </button>
              </div>

              {/* Export Button */}
              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Export
              </button>

              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Filters */}
          <div className={`border-r border-gray-200 bg-gray-50 flex-shrink-0 transition-all ${showFilters ? 'w-64' : 'w-0'}`}>
            {showFilters && (
              <div className="h-full overflow-y-auto p-4">
                {/* Buildings */}
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Buildings</h3>
                  <div className="space-y-1">
                    {buildings.map(building => (
                      <label key={building} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedBuildings.has(building)}
                          onChange={() => toggleBuilding(building)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-sm text-gray-700">{building}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Categories */}
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Work Categories</h3>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {categories.map(category => (
                      <label key={category} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCategories.has(category)}
                          onChange={() => toggleCategory(category)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-sm text-gray-700 truncate">{category}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Peak Summary */}
                {peakResources.maxTotal > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      Peak Resources
                    </div>
                    <div className="text-2xl font-bold text-amber-800">{peakResources.maxTotal} workers</div>
                    <div className="text-xs text-amber-600">on {peakResources.peakDate}</div>
                    <div className="mt-2 space-y-1">
                      {Object.entries(peakResources.laborPeaks).slice(0, 5).map(([labor, { count }]) => (
                        <div key={labor} className="flex justify-between text-xs">
                          <span className="text-gray-600">{labor}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-white border border-gray-200 rounded-r-lg p-1 shadow-sm hover:bg-gray-50 z-10"
            style={{ left: showFilters ? '256px' : '0' }}
          >
            <Filter className="w-4 h-4 text-gray-500" />
          </button>

          {/* Gantt Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {workGroups.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Select buildings and categories to view resource timeline</p>
                </div>
              </div>
            ) : (
              <>
                {/* Timeline Header */}
                <div className="border-b border-gray-200 bg-gray-50 flex-shrink-0 overflow-hidden">
                  <div className="flex">
                    <div className="w-64 flex-shrink-0 px-4 py-2 border-r border-gray-200 font-medium text-sm text-gray-700">
                      Work Category / Floor
                    </div>
                    <div ref={timelineRef} className="flex-1 overflow-x-auto">
                      <div className="flex" style={{ width: `${timePeriods.length * columnWidth}px` }}>
                        {timePeriods.map((period, idx) => (
                          <div
                            key={idx}
                            className="flex-shrink-0 px-2 py-2 text-center text-xs font-medium text-gray-600 border-r border-gray-200"
                            style={{ width: `${columnWidth}px` }}
                          >
                            {period.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gantt Rows */}
                <div className="flex-1 overflow-auto">
                  <div className="min-h-full">
                    {/* Group by Building */}
                    {Array.from(new Set(workGroups.map(g => g.building))).map(building => {
                      const buildingGroups = workGroups.filter(g => g.building === building);
                      
                      return (
                        <div key={building} className="border-b border-gray-200">
                          {/* Building Header */}
                          <div className="flex bg-gray-100 sticky top-0 z-10">
                            <div className="w-64 flex-shrink-0 px-4 py-2 border-r border-gray-200 font-semibold text-sm text-gray-800 flex items-center gap-2">
                              <ChevronDown className="w-4 h-4" />
                              {building}
                              <span className="text-xs font-normal text-gray-500">({buildingGroups.length} groups)</span>
                            </div>
                            <div className="flex-1"></div>
                          </div>

                          {/* Work Groups */}
                          {buildingGroups.map(group => {
                            const isExpanded = expandedGroups.has(group.id);
                            const barStyle = getBarStyle(group.startDate, group.endDate);
                            const topLabors = Object.entries(group.resources)
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 3);

                            return (
                              <div key={group.id} className="border-b border-gray-100 last:border-b-0">
                                {/* Group Row */}
                                <div className="flex hover:bg-gray-50">
                                  <div 
                                    className="w-64 flex-shrink-0 px-4 py-3 border-r border-gray-200 cursor-pointer"
                                    onClick={() => toggleGroup(group.id)}
                                  >
                                    <div className="flex items-center gap-2">
                                      {isExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                      )}
                                      <div>
                                        <div className="font-medium text-sm text-gray-800">{group.category}</div>
                                        <div className="text-xs text-gray-500">{group.floor} • {group.tasks.length} tasks</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-1 relative py-2 overflow-hidden" style={{ minWidth: `${timePeriods.length * columnWidth}px` }}>
                                    {/* Gantt Bar */}
                                    <div
                                      className="absolute h-8 bg-emerald-500 rounded shadow-sm flex items-center px-2 overflow-hidden"
                                      style={{ ...barStyle, top: '50%', transform: 'translateY(-50%)' }}
                                    >
                                      <span className="text-xs text-white font-medium truncate">
                                        {topLabors.map(([l, d]) => `${Math.ceil(d / Math.max(1, Math.ceil((group.endDate.getTime() - group.startDate.getTime()) / (24 * 60 * 60 * 1000))))}${l.charAt(0)}`).join(' ')}
                                      </span>
                                    </div>
                                    {/* Grid lines */}
                                    <div className="absolute inset-0 flex pointer-events-none">
                                      {timePeriods.map((_, idx) => (
                                        <div key={idx} className="border-r border-gray-100" style={{ width: `${columnWidth}px` }} />
                                      ))}
                                    </div>
                                  </div>
                                </div>

                                {/* Expanded Details */}
                                {isExpanded && (
                                  <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
                                    <div className="ml-8 grid grid-cols-2 gap-4">
                                      <div>
                                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Duration</div>
                                        <div className="text-sm">
                                          {group.startDate.toLocaleDateString()} - {group.endDate.toLocaleDateString()}
                                          <span className="text-gray-500 ml-2">
                                            ({Math.ceil((group.endDate.getTime() - group.startDate.getTime()) / (24 * 60 * 60 * 1000))} days)
                                          </span>
                                        </div>
                                      </div>
                                      <div>
                                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Resources Required</div>
                                        <div className="flex flex-wrap gap-2">
                                          {Object.entries(group.resources).map(([labor, days]) => (
                                            <span key={labor} className={`px-2 py-1 rounded text-xs text-white ${getLaborColor(labor)}`}>
                                              {labor}: {days} days
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer - Resource Summary */}
        {workGroups.length > 0 && (
          <div className="border-t-2 border-gray-200 bg-gray-50 px-6 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Total Worker-Days:</span>
                  <span className="font-bold text-gray-900">
                    {workGroups.reduce((sum, g) => sum + g.totalWorkerDays, 0).toLocaleString()}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  Peak: <span className="font-semibold text-amber-600">{peakResources.maxTotal} workers</span> on {peakResources.peakDate}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {Object.entries(peakResources.laborPeaks).slice(0, 4).map(([labor, { count }]) => (
                  <span key={labor} className={`px-2 py-1 rounded text-xs text-white ${getLaborColor(labor)}`}>
                    {labor}: {count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceGanttView;

